/* @jsx h */
import { defineComponent, h, ref, watch } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { Run } from "../shared/types";

type RunStep = Run["steps"][number];
export type AnswerInput = { checked?: boolean | null; value_number?: number | null; value_text?: string | null };

type Props = {
  step: RunStep;
  index: number;
  readOnly: boolean;
  saving: boolean;
  onAnswer: (answer: AnswerInput) => void;
};

const rangeText = (s: RunStep) =>
  s.min_value != null && s.max_value != null
    ? `${s.min_value}–${s.max_value}${s.unit ? ` ${s.unit}` : ""}`
    : s.min_value != null
      ? `≥ ${s.min_value}${s.unit ? ` ${s.unit}` : ""}`
      : s.max_value != null
        ? `≤ ${s.max_value}${s.unit ? ` ${s.unit}` : ""}`
        : null;

// One step in a run: a big tick, yes/no buttons, a reading with its range, or a note.
export const StepAnswer = defineComponent<Props>({
  name: "StepAnswer",
  props: ["step", "index", "readOnly", "saving", "onAnswer"],
  setup(props) {
    const { t } = useLocalization();
    const draft = ref("");
    watch(
      () => props.step.answer,
      (a) => {
        draft.value = a?.value_number != null ? String(a.value_number) : (a?.value_text ?? "");
      },
      { immediate: true },
    );

    const commitNumber = () => {
      const v = draft.value.trim().replace(",", ".");
      const current = props.step.answer?.value_number ?? null;
      const next = v === "" ? null : Number(v);
      if (next !== null && !Number.isFinite(next)) return;
      if (next !== current) props.onAnswer({ value_number: next });
    };
    const commitText = () => {
      const next = draft.value.trim() || null;
      if (next !== (props.step.answer?.value_text ?? null)) props.onAnswer({ value_text: next });
    };

    return () => {
      const s = props.step;
      const a = s.answer;
      const done = a != null && (s.kind !== "check" || a.checked);
      const issue = a?.is_issue ?? false;
      const range = rangeText(s);

      const control = () => {
        switch (s.kind) {
          case "check":
            return null;
          case "yes_no":
            return (
              <div class="join">
                {[true, false].map((v) => (
                  <button
                    key={String(v)}
                    class={[
                      "btn join-item btn-sm min-w-16",
                      a?.checked === v ? (v ? "btn-success" : "btn-error") : "btn-ghost border-base-300",
                    ].join(" ")}
                    disabled={props.readOnly || props.saving}
                    onClick={() => props.onAnswer({ checked: a?.checked === v ? null : v })}
                  >
                    {v ? t("workday.common.yes") : t("workday.common.no")}
                  </button>
                ))}
              </div>
            );
          case "temperature":
          case "number":
            return (
              <div class="flex items-center gap-2">
                <input
                  type="text"
                  inputmode="decimal"
                  class={["input input-bordered input-sm w-24 text-right text-base font-semibold", issue ? "input-error" : a ? "input-success" : ""].join(" ")}
                  disabled={props.readOnly}
                  value={draft.value}
                  onInput={(e: Event) => (draft.value = (e.target as HTMLInputElement).value)}
                  onBlur={commitNumber}
                  onKeydown={(e: KeyboardEvent) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
                />
                {s.unit && <span class="text-sm text-base-content/60">{s.unit}</span>}
              </div>
            );
          case "text":
            return (
              <textarea
                class="textarea textarea-bordered textarea-sm w-full"
                rows={2}
                disabled={props.readOnly}
                value={draft.value}
                onInput={(e: Event) => (draft.value = (e.target as HTMLTextAreaElement).value)}
                onBlur={commitText}
              />
            );
        }
      };

      return (
        <div
          class={[
            "rounded-xl border p-3 transition",
            issue ? "border-error/50 bg-error/5" : done ? "border-success/40 bg-success/5" : "border-base-300 bg-base-100",
          ].join(" ")}
        >
          <div class="flex items-start gap-3">
            {s.kind === "check" ? (
              <button
                class={[
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition",
                  a?.checked ? "border-success bg-success text-success-content" : "border-base-300 hover:border-success",
                ].join(" ")}
                disabled={props.readOnly || props.saving}
                aria-label={s.title}
                onClick={() => props.onAnswer({ checked: a?.checked ? null : true })}
              >
                {a?.checked && <span class="material-symbols-outlined text-lg">check</span>}
              </button>
            ) : (
              <span
                class={[
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
                  issue ? "bg-error text-error-content" : done ? "bg-success text-success-content" : "bg-base-200 text-base-content/60",
                ].join(" ")}
              >
                {issue ? "!" : done ? <span class="material-symbols-outlined text-lg">check</span> : props.index + 1}
              </span>
            )}
            <div class="min-w-0 flex-1 space-y-2">
              <div class="flex flex-wrap items-baseline justify-between gap-2">
                <span class={["font-medium", s.kind === "check" && a?.checked ? "text-base-content/60 line-through" : ""].join(" ")}>
                  {s.title}
                  {!s.required && <span class="ml-2 text-xs font-normal text-base-content/40">{t("workday.common.optional")}</span>}
                </span>
                {range && (
                  <span class={["text-xs", issue ? "font-semibold text-error" : "text-base-content/50"].join(" ")}>
                    {issue ? t("workday.runner.out_of_range").replace("{range}", range) : t("workday.runner.target").replace("{range}", range)}
                  </span>
                )}
              </div>
              {control()}
              {a?.answered_by_name && (
                <div class="text-[11px] text-base-content/40">
                  {a.answered_by_name} · {new Date(a.answered_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };
  },
});
